import { draftKey } from "./store";

const rowsOf = (conflicts = []) =>
  conflicts.map(({ type, accessor, id }) => ({
    accessor: type ?? accessor,
    id
  }));

// "Discard my changes and reload" of the conflict dialog: every conflict is
// resolved in its own scope. The stored rows are fetched first (a 409 has no
// data; reloadItems applies a row no draft holds and gives a drafted row's
// scope the stored row as a conflict); then each drafted row gives up its
// changes for the stored row (reloadConflicts). The other changes of the
// drafts stay, except the drafts with rows changed in one structural
// operation with a row reloaded (deps, dependentsOf: e.g. a new variant and
// its endpoint): they are discarded whole, so that no orphan block nor
// dangling tree node is left. A row no draft holds (the row of an open
// form, e.g. the property whose tree the form changed) gets the stored row
// the form holds (reloadHeld: after reloadItems, which holds it again while
// the form is dirty), and discards the drafts whose rows depend on it the
// same way.
// conflicts: the 409's ({ type, id }) or the live ones ({ accessor, id,
// scope }).
// Resolves { discarded: [scope] }, the drafts discarded whole.
// Rejects (nothing reloaded) when the stored rows could not be fetched
// (reloadItems resolves { failed }): the dialog stays open and says so.
export async function resolveConflicts(store, conflicts = [], reloadItems) {
  const rows = rowsOf(conflicts);

  const { failed = [] } = (await reloadItems(rows)) ?? {};
  if (failed.length) {
    throw new Error(
      `The stored ${failed
        .map(({ accessor, id }) => `${accessor} ${id}`)
        .join(", ")} could not be loaded: try again.`
    );
  }

  const discarded = new Set();
  for (const { accessor, id } of rows) {
    const key = draftKey(accessor, id);
    // A draft discarded whole by a row before holds this one no more
    const scope = store.ownerOf(accessor, id);
    if (!scope) {
      store.reloadHeld(accessor, id);
      const wholes = store.dependentsOf([key]).map((row) => row.scope);
      for (const whole of new Set(wholes)) {
        store.discard(whole);
        discarded.add(whole);
      }
      continue;
    }
    const result = store.reloadConflicts(scope, [key]);
    for (const whole of result?.discarded ?? []) discarded.add(whole);
  }

  return { discarded: [...discarded] };
}

// The created rows that "Discard my changes and reload" of these conflicts
// discards with their whole draft (they depend on a conflicting row):
// [{ scope, accessor, id }]. The conflict dialog says so.
export const dependentsOfConflicts = (store, conflicts = []) =>
  store.dependentsOf(
    rowsOf(conflicts).map(({ accessor, id }) => draftKey(accessor, id))
  );

// The conflicts touch the row of a form (its id, or the id it had before a
// rename not saved yet).
export const touchesRow = (conflicts = [], accessor, ids = []) =>
  conflicts.some(
    ({ type, accessor: table, id }) =>
      (type ?? table) === accessor && ids.includes(id)
  );
